import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  FileText, 
  Plus, 
  MoreVertical, 
  Send, 
  Eye,
  Pencil,
  Download,
  BarChart3,
  Loader2
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

interface TenantReportsTabProps {
  tenantId: string;
  tenantName: string;
}

interface Report {
  id: string;
  month: string;
  status: string | null;
  draft_content: any;
  edited_content: any;
  sent_at: string | null;
  created_at: string | null;
}

const statusColors: Record<string, string> = {
  draft: 'bg-gray-500/10 text-gray-500',
  pending_review: 'bg-yellow-500/10 text-yellow-500',
  approved: 'bg-blue-500/10 text-blue-500',
  sent: 'bg-green-500/10 text-green-500',
};

const TenantReportsTab: React.FC<TenantReportsTabProps> = ({ tenantId, tenantName }) => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const fetchReports = async () => {
    try {
      const { data, error } = await supabase
        .from('monthly_reports')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('month', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [tenantId]);

  const handleGenerateDraft = async () => {
    setGenerating(true);
    try {
      // Get current month in YYYY-MM format
      const currentMonth = new Date().toISOString().slice(0, 7);
      
      // Check if report already exists for this month
      const existingReport = reports.find(r => r.month === currentMonth);
      if (existingReport) {
        toast.error('Report for this month already exists');
        return;
      }

      // Create draft report with placeholder content
      const { error } = await supabase
        .from('monthly_reports')
        .insert({
          tenant_id: tenantId,
          month: currentMonth,
          status: 'draft',
          draft_content: {
            summary: `Monthly report for ${tenantName}`,
            generated_at: new Date().toISOString(),
            metrics: {
              total_conversations: 0,
              avg_satisfaction: 0,
              escalation_rate: 0,
            },
            highlights: [],
            recommendations: [],
          },
        });

      if (error) throw error;
      toast.success('Draft report generated');
      fetchReports();
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const formatMonth = (monthStr: string) => {
    const date = new Date(monthStr + '-01');
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Monthly Reports</h3>
          <p className="text-sm text-muted-foreground">
            Generate and send monthly performance reports
          </p>
        </div>
        <Button onClick={handleGenerateDraft} disabled={generating}>
          {generating ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Plus className="w-4 h-4 mr-2" />
          )}
          Generate Draft
        </Button>
      </div>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Report History</CardTitle>
        </CardHeader>
        <CardContent>
          {reports.length === 0 ? (
            <div className="text-center py-12">
              <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No reports yet</h3>
              <p className="text-muted-foreground mt-1 mb-4">
                Generate your first monthly report
              </p>
              <Button onClick={handleGenerateDraft} disabled={generating}>
                {generating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Generate Draft
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">
                        {formatMonth(report.month)}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="secondary"
                          className={statusColors[report.status || 'draft']}
                        >
                          {report.status || 'draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {report.created_at 
                          ? new Date(report.created_at).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {report.sent_at 
                          ? new Date(report.sent_at).toLocaleDateString()
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF
                            </DropdownMenuItem>
                            {report.status !== 'sent' && (
                              <DropdownMenuItem>
                                <Send className="w-4 h-4 mr-2" />
                                Send to Client
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantReportsTab;
